import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import { Lock, LogIn, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

const LAST_KEY = "nex_last_login";
const readLast = () => { try { return JSON.parse(localStorage.getItem(LAST_KEY) || "null") || {}; } catch { return {}; } };

const LoginPage = () => {
  const last = readLast();
  const [mode, setMode] = useState(last.has_pin ? "pin" : "password"); // "password" | "pin"
  const [email, setEmail] = useState(last.email || "");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, pinLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = mode === "password" ? await login(email, password) : await pinLogin(email, pin);
      try { localStorage.setItem(LAST_KEY, JSON.stringify({ email: user.email, has_pin: !!user.has_pin })); } catch {}
      toast.success(`Welcome back, ${user.name.split(" ")[0]}.`);
      if (!user.has_pin) {
        toast("Set a 6-digit PIN for faster sign-in next time.", { duration: 6000 });
        navigate("/admin/settings", { replace: true });
      } else navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const startGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/auth/callback";
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
      <div className="absolute inset-0 blueprint-bg opacity-40 pointer-events-none" />
      <div className="w-full max-w-md relative">
        <Link to="/" className="flex items-center justify-center gap-4 mb-10">
          <img src={LIFE_LOGO} alt="LIFE" className="h-14 w-14 object-contain" />
          <div className="w-px h-12 bg-nex-border" />
          <div>
            <NexWordmark size="text-3xl" />
            <div className="mono-body text-[10px] tracking-[0.28em] uppercase mt-1">Owner Console</div>
          </div>
        </Link>

        <form onSubmit={submit} className="card-industrial corner-tick p-8 md:p-10 space-y-6">
          <div className="text-center">
            <Lock size={22} className="text-nex-gold mx-auto mb-3" />
            <div className="mono-label mb-2">// SECURE ACCESS</div>
            <h1 className="serif-head text-3xl">Sign in</h1>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 bg-black/30 p-1 rounded" data-testid="login-mode-tabs">
            <button
              type="button"
              data-testid="login-tab-password"
              onClick={() => setMode("password")}
              className={`flex-1 py-2 mono-label transition-colors ${mode === "password" ? "bg-nex-gold/20 text-nex-gold" : "text-nex-chrome hover:text-nex-silver"}`}
            >
              Password
            </button>
            <button
              type="button"
              data-testid="login-tab-pin"
              onClick={() => setMode("pin")}
              className={`flex-1 py-2 mono-label transition-colors ${mode === "pin" ? "bg-nex-gold/20 text-nex-gold" : "text-nex-chrome hover:text-nex-silver"}`}
            >
              PIN
            </button>
          </div>

          <div>
            <label className="mono-label block mb-2">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-industrial"
              placeholder="you@nexengines.com"
              required
              autoComplete="username"
            />
          </div>

          {mode === "password" ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="mono-label">Password</label>
                <Link
                  to="/admin/forgot-password"
                  data-testid="forgot-password-link"
                  className="mono-body text-[11px] text-nex-gold hover:underline"
                >
                  Forgot?
                </Link>
              </div>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-industrial"
                required
                autoComplete="current-password"
              />
            </div>
          ) : (
            <div>
              <label className="mono-label block mb-2">6-digit PIN</label>
              <input
                data-testid="login-pin"
                type="password"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="input-industrial tracking-[0.5em] text-center text-xl"
                placeholder="••••••"
                required
              />
              <div className="mono-body text-[10px] text-nex-muted mt-2">
                Set your PIN in System → PIN &amp; Security after signing in with your password once.
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || (mode === "pin" && pin.length !== 6)}
            data-testid="login-submit"
            className="tick-btn-primary w-full justify-center disabled:opacity-50"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : (
              mode === "password"
                ? <>Sign In <LogIn size={16} /></>
                : <>Unlock with PIN <KeyRound size={16} /></>
            )}
          </button>

          {/* Google Workspace SSO — restricted to pre-approved admins */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-nex-border" />
            <div className="mono-body text-[10px] uppercase tracking-[0.25em] text-nex-muted">or</div>
            <div className="flex-1 h-px bg-nex-border" />
          </div>

          <button
            type="button"
            onClick={startGoogleSignIn}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-nex-border bg-white/5 hover:bg-white/10 text-nex-silver mono-label transition-colors"
          >
            <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.3C29.5 34.9 26.9 36 24 36c-5.3 0-9.7-3.1-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.5 5.3C41.6 35.6 44 30.2 44 24c0-1.2-.1-2.4-.4-3.5z"/>
            </svg>
            Sign in with Google
          </button>
          <div className="mono-body text-[10px] text-center text-nex-muted -mt-3">
            Restricted to pre-approved <span className="text-nex-gold">@nexengines.com</span> workspace accounts
          </div>

          <div className="mono-body text-[10px] uppercase tracking-[0.25em] text-nex-muted text-center pt-2">
            Access restricted · NEX Owner Console
          </div>
        </form>

        <Link to="/" className="block text-center mt-6 mono-body text-[11px] text-nex-chrome hover:text-nex-gold">
          ← Back to nexengines.com
        </Link>
      </div>
    </div>
  );
};

export default LoginPage;
